import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Link } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import { DisplayFieldSameLine } from "../util/DisplayField";

import { goToPolicyPage, goToPolicyListLookup } from "./utils";
import moment from "moment";

import { tableCellRender, getCompanyCodeName } from "../util/tableUtils";
import { handleNullValue } from "../util/tableUtils";

import { useClientConfig } from "../../context/clientConfigContext";

const useStyles = makeStyles(() => ({
  copyableLink: {
    userSelect: "text",
  },
  typographyWithHover: {
    marginLeft: "4px",
  },
}));

const formatDate = (date) => {
  const DATETIME_FORMAT = process.env.REACT_APP_DATETIME_FORMAT;
  return date ? new Date(date).toLocaleDateString(DATETIME_FORMAT) : "";
};

// TODO: remove this
const DisplayField = (props) => (
  <Box m={0.05} mb="0.5em">
    <Typography variant="body2" display="block" component={"span"}>
      <Box sx={{ fontWeight: 600 }}>{props.label}</Box>
    </Typography>
    <Typography variant="caption" gutterBottom component={"span"}>
      {tableCellRender(props.value)}
    </Typography>
  </Box>
);

const displayOneOrTwoCityCodes = (oldValue, newValue) => {
  if (oldValue === newValue) return oldValue;
  else return `${handleNullValue(oldValue)} ⟶ ${newValue}`;
};

export const PolicyDescription = (props) => {
  const { t } = useTranslation();
  const { policy } = props;
  const classes = useStyles();
  const config = useClientConfig();

  const policyHasAddress = (policy) => {
    if (
      !policy?.addressStreetName &&
      !policy?.addressCity &&
      !policy?.addressCityCode &&
      !policy?.addressZIPCode &&
      !policy?.addressProvince
    ) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <>
      <Box display="flex">
        <Box flex={1} mr="0.5em">
          <div>
            <Typography variant="body2" display="block" component={"span"}>
              <Box sx={{ fontWeight: 600 }}>{t("idLocation")}</Box>
            </Typography>
            <Link
              component="button"
              variant="body2"
              className={classes.copyableLink}
              onClick={() => {
                goToPolicyPage(
                  policy.policyNumber,
                  policy.policyNumberSecondary,
                  policy.multilocationID,
                  policy.locationStartDate,
                );
              }}
            >
              {policy.multilocationID}
            </Link>
          </div>
        </Box>

        <Box flex={1} mr="0.5em">
          <div>
            <Typography variant="body2" display="block" component={"span"}>
              <Box sx={{ fontWeight: 600 }}>{t("policy.id")}</Box>
            </Typography>
            <Link
              component="button"
              variant="body2"
              className={classes.copyableLink}
              onClick={() => {
                goToPolicyListLookup(policy.policyNumberSecondary, policy.companyCode);
              }}
            >
              {policy.policyNumberSecondary ? policy.policyNumberSecondary : "-"}
            </Link>
          </div>
        </Box>

        <Box flex={1} mr="0.5em">
          <DisplayField
            label={t("company")}
            value={getCompanyCodeName(policy.companyCode, config.companyCodesMap)}
          />
        </Box>
      </Box>

      <Box display="flex">
        <Box flex={1} mr="0.5em">
          <DisplayFieldSameLine
            label={t("policy.locationActive")}
            value={`${policy.locationActiveFlag} (${moment(policy.locationStartDate).format("DD/MM/YYYY")}-${moment(policy.locationEndDate).format("DD/MM/YYYY")})`}
          />
        </Box>
      </Box>

      <DisplayFieldSameLine label={t("commercialProduct")} value={policy.productCommercial} />
      {config.tableColumns.coinsuranceType && (
        <DisplayFieldSameLine label={t("coinsuranceProduct")} value={policy.coinsuranceType} />
      )}
      <DisplayFieldSameLine label={t("productGroup")} value={policy.productSireGroup} />
      <DisplayFieldSameLine label={t("productCategory")} value={policy.sire} />
      <DisplayFieldSameLine
        label={t("agency")}
        value={policy.agencyCode && policy.agencyName && policy.agencyCode + " - " + policy.agencyName}
      />

      <Box display="flex">
        <Box flex={1} mr="0.5em">
          <DisplayField label={t("policy.inceptionDate")} value={formatDate(policy.inceptionDate)} />
        </Box>
        <Box flex={1} mr="0.5em">
          <DisplayField label={t("expirationDate")} value={formatDate(policy.expiryDate)} />
        </Box>
      </Box>

      <DisplayField
        label={t("address")}
        value={`${policy.addressStreetName ? policy.addressStreetName : ""} ${
          policy.addressBuildingNo ? policy.addressBuildingNo : ""
        }`}
      />

      <Box display="flex">
        <Box flex={1} mr="0.5em">
          <DisplayField label={t("city")} value={policy.addressCity} />
        </Box>
        <Box flex={1} mr="0.5em">
          <DisplayField
            label={t("cityCode")}
            value={displayOneOrTwoCityCodes(policy.addressCityCode, policy.addressCityCodeAdjusted)}
          />
        </Box>
      </Box>

      <Box display="flex">
        <Box flex={1} mr="0.5em">
          <DisplayField label={t("zip")} value={policy.addressZIPCode} />
        </Box>
        <Box flex={1} mr="0.5em">
          <DisplayField label={t("county")} value={policy.addressProvince} />
        </Box>
      </Box>

      {!policyHasAddress(policy) && (
        <Alert variant="outlined" severity="warning">
          {/* TODO: add default address based on client */}
          {getCompanyCodeName(policy.companyCode, config.companyCodesMap) === "RMA"
            ? t("policy.addressNotAvailableRMA")
            : t("policy.addressNotAvailableITA")}
        </Alert>
      )}

      {policyHasAddress(policy) && !policy.location.coordinates[0] && (
        <Alert variant="outlined" severity="warning">
          Internal error: Address is given above, but it is missing in the geolocation database. It is
          probably not updated. Please try again later.
        </Alert>
      )}
    </>
  );
};
